// =============================================================================
// src/data/analytics-overlay-format.ts — Formatação pt-BR e normalização de
// plataforma do Analytics live. Datas são derivadas das STRINGS ISO do backend
// (sem new Date local) para o rótulo não depender do fuso do browser.
// =============================================================================

const ANALYTICS_PT_BR_MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'] as const;
const ANALYTICS_PT_BR_MONTHS_FULL = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

/** 10385 → '10.385' (separador de milhar pt-BR). */
export function analyticsPtBrInteger(integerValue: number): string {
  return Math.round(integerValue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Estilo dos totais do mock: 87400 → '87,4 mil'; 658000 → '658 mil'; 1200000 → '1,2 mi'. */
export function analyticsPtBrCompactNumber(compactValue: number): string {
  if (compactValue < 1000) return analyticsPtBrInteger(compactValue);
  const inThousands = compactValue < 1_000_000;
  const scaled = compactValue / (inThousands ? 1000 : 1_000_000);
  const rounded = Math.round(scaled * 10) / 10;
  const digits = rounded >= 100 || Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1).replace('.', ',');
  return `${digits} ${inThousands ? 'mil' : 'mi'}`;
}

/**
 * Enum do backend → label EXATO da UI (os filtros comparam
 * data-analytics-platform com data-analytics-platform-filter por string).
 * `web` + kind `news` = Google News RSS → 'Noticias'.
 */
export function analyticsPlatformUiLabelFor(rawPlatform: string, contentKind: string): string {
  if (rawPlatform === 'web' && contentKind === 'news') return 'Noticias';
  const byPlatform: Record<string, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    facebook: 'Facebook',
    x: 'X/Twitter',
    youtube: 'YouTube',
    web: 'Web',
  };
  return byPlatform[rawPlatform] ?? `${rawPlatform.charAt(0).toUpperCase()}${rawPlatform.slice(1)}`;
}

/** Todas as plataformas cruas coletadas (ordem estável p/ queries _in). */
export const ANALYTICS_ALL_RAW_PLATFORMS: readonly string[] = ['instagram', 'tiktok', 'facebook', 'x', 'web'];

/** Label da UI → plataformas cruas do backend (para o drill-down no servidor).
 * 'Todos' = todas; canal sem coleta (Blogs) = lista vazia. */
export function analyticsRawPlatformsForUiLabel(uiLabel: string): readonly string[] {
  if (uiLabel === 'Todos') return ANALYTICS_ALL_RAW_PLATFORMS;
  const byLabel: Record<string, readonly string[]> = {
    Instagram: ['instagram'],
    TikTok: ['tiktok'],
    Facebook: ['facebook'],
    'X/Twitter': ['x'],
    Noticias: ['web'],
    Web: ['web'],
    YouTube: ['youtube'],
  };
  return byLabel[uiLabel] ?? [];
}

/** '2026-07-24' → '24 jul'. Dia inválido cai no próprio texto (nunca derruba o render). */
export function analyticsDayLabel(isoDay: string): string {
  const monthName = analyticsMonthShortFor(isoDay);
  if (!monthName) return isoDay;
  return `${Number(isoDay.slice(8, 10))} ${monthName}`;
}

/** '2026-07-24' → '24 jul 2026'. */
export function analyticsDayFullLabel(isoDay: string): string {
  const monthName = analyticsMonthShortFor(isoDay);
  if (!monthName) return isoDay;
  return `${Number(isoDay.slice(8, 10))} ${monthName} ${isoDay.slice(0, 4)}`;
}

/** '2026-07' → 'Jul' (label curto do eixo de meses). */
export function analyticsMonthLabel(isoMonth: string): string {
  const monthIndex = Number(isoMonth.slice(5, 7)) - 1;
  const monthFull = ANALYTICS_PT_BR_MONTHS_FULL[monthIndex];
  if (!monthFull) return isoMonth;
  return monthFull.slice(0, 3);
}

/** '2026-07' → 'Julho de 2026'. */
export function analyticsMonthFullLabel(isoMonth: string): string {
  const monthFull = ANALYTICS_PT_BR_MONTHS_FULL[Number(isoMonth.slice(5, 7)) - 1];
  if (!monthFull) return isoMonth;
  return `${monthFull} de ${isoMonth.slice(0, 4)}`;
}

/** '2026-07-24T12:34:00Z' → '24 jul, 12h34' (partes UTC da string — determinístico). */
export function analyticsTimestampLabel(isoTimestamp: string): string {
  const dayPart = analyticsDayLabel(isoTimestamp.slice(0, 10));
  const hourPart = isoTimestamp.slice(11, 13);
  const minutePart = isoTimestamp.slice(14, 16);
  if (!hourPart || !minutePart) return dayPart;
  return `${dayPart}, ${hourPart}h${minutePart}`;
}

function analyticsMonthShortFor(isoDay: string): string | null {
  return ANALYTICS_PT_BR_MONTHS_SHORT[Number(isoDay.slice(5, 7)) - 1] ?? null;
}

/** Quanto do caminho cabe no rotulo antes de virar ruido (medido no comp). */
const ANALYTICS_SOURCE_LABEL_MAX_PATH = 32;

/**
 * Rotulo LEGIVEL da fonte a partir da URL. O href continua sendo a URL inteira.
 *
 * Defeito de 2026-09-20: `sourceUrlLabel` fazia dupla funcao — era o texto
 * exibido E o href do botao "Visitar". No mock o campo trazia rotulo curto
 * ("oglobo.globo.com/brasil/br319-amazonas"), mas o overlay vivo atribui a URL
 * CRUA (analytics-overlay-feed:137). Uma noticia do Google News chega com 300+
 * caracteres de base64 sem espaco nenhum, e o bloco da fonte esticou para
 * 1.881 px dentro de uma coluna de 550 px, pintando por cima do painel
 * "Resumo salvo". Rotulo e endereco sao coisas diferentes; aqui sai o rotulo.
 *
 * Valor que nao e URL atravessa intacto — e o caso dos rotulos ja curtos do mock.
 *
 * @example analyticsSourceLabelFromUrl('https://news.google.com/rss/articles/CBMi...oc=5')
 * // 'news.google.com/rss/articles/CBMi…'
 * @example analyticsSourceLabelFromUrl('tiktok.com/@omaraziz/video/br319')
 * // 'tiktok.com/@omaraziz/video/br319'
 */
export function analyticsSourceLabelFromUrl(sourceUrl: string): string {
  if (!sourceUrl.startsWith('http')) return sourceUrl;
  const semProtocolo = sourceUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const corte = semProtocolo.indexOf('/');
  if (corte < 0) return semProtocolo;
  const host = semProtocolo.slice(0, corte);
  const caminho = semProtocolo.slice(corte).replace(/\/$/, '');
  if (caminho.length <= ANALYTICS_SOURCE_LABEL_MAX_PATH) return `${host}${caminho}`;
  return `${host}${caminho.slice(0, ANALYTICS_SOURCE_LABEL_MAX_PATH)}…`;
}

/** Entidades que a coleta traz de verdade, medidas no corpus em 2026-09-20:
 *  &nbsp; (10.553), &amp; (104), &gt; (49), &lt; (8). As aspas entram por
 *  simetria — custam uma linha e aparecem na mesma classe de payload. */
const ANALYTICS_HTML_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", '#160': ' ',
};

/**
 * Decodifica entidades HTML do texto coletado.
 *
 * Defeito de 2026-09-20: 7.724 dos 127.025 itens visiveis (6%) chegam do RSS com
 * entidade CRUA no corpo. O markup escapa tudo antes de renderizar — correto — e
 * o resultado era o leitor vendo "Roberto Cidade &nbsp;&nbsp; Amazonas1" na tela.
 * Decodificar aqui, na normalizacao do dado, e nao no markup: quem renderiza
 * continua escapando, entao `&lt;script&gt;` vira `<script>` aqui e volta a
 * `&lt;script&gt;` na tela — texto, nunca marcacao.
 *
 * Passada UNICA de proposito: decodificar em etapas faria "&amp;lt;" virar "<".
 *
 * @example analyticsDecodeHtmlEntities('Cidade &nbsp;&nbsp; Amazonas1') // 'Cidade   Amazonas1'
 */
export function analyticsDecodeHtmlEntities(rawText: string): string {
  return rawText.replace(/&(#?[a-zA-Z0-9]+);/g, (entidadeInteira, nome: string) =>
    ANALYTICS_HTML_ENTITIES[nome] ?? ANALYTICS_HTML_ENTITIES[nome.toLowerCase()] ?? entidadeInteira);
}
